'use strict'
const SyncClient = require('twilio-sync').SyncClient;
const EventEmitter = require('events').EventEmitter;

// Creating a global version of "this" to be used in functions
const _this = this;


/**
 * Constructor for SP Twilio Sync Client
 * @param {Object} config - The JSON Object to construct the SP Twilio Sync Client
 * @param {string} config.baseUri - Base URI to the SP API for requests
 * @param {string} config.documentId - Name of the Twilio Sync Doc to manage
 * @param {string} config.identity - Unique name for the Twilio Sync Client
 * @param {string} config.token - Valid SP Token
 * @param {Object} [config.initialData] - Optional JSON data that will be put into the sync doc upon creation
 * @returns {Promise<TwilioSyncClient>}
 * @constructor
 */
const TwilioSyncClient = function (config) {
    // Validate that the params sent in are enough to wrap the twilio sync client
    const isValid = validateParams(config);
    if (!isValid) {
        throw 'Invalid params to setup SP Sync Doc Client';
    }

    // Set client fields from constructor params
    _this.baseUri = config.baseUri;
    _this.documentId = config.documentId;
    _this.identity = config.identity;
    _this.token = config.token;
    if (config.initialData) {
        _this.initialData = config.initialData;
    }

    // Instantiate needed objects
    _this.syncClient = false;
    _this.eventEmitter = new EventEmitter();
    _this.syncToken = null;

    // Because this is a constructor, we need to return an instance of this component
    const component = this;
    // Initialize the client
    return init().then(() => {
        console.log('Twilio Sync Client Initialized!');
        return component;
    });
}

/**
 * Validates params before constructing the client object
 * @param {Object} config - The JSON Object to construct the SP Twilio Sync Client
 * @param {string} config.baseUri - Base URI to the SP API for requests
 * @param {string} config.documentId - Name of the Twilio Sync Doc to manage
 * @param {string} config.identity - Unique name for the Twilio Sync Client
 * @param {string} config.token - Valid SP Token
 * @param {Object} [config.initialData] - Optional JSON data that will be put into the sync doc upon creation
 * @returns {boolean} - Returns a boolean of whether the validation is successful or not
 */
function validateParams (config) {
    // First check all params that are needed by all sync doc types
    if (!config.baseUri) {
        return false;
    }
    if (!config.documentId) {
        return false;
    }
    if (!config.identity) {
        return false;
    }
    if (!config.token) {
        return false;
    }

    // Check if the optional initialData field is of type "object"
    if (config.initialData && typeof config.initialData !== "object") {
        return false;
    }

    return true;
}

/**
 * Calls out the SP API to get a Twilio Sync Token
 * @returns {Promise<string>} - Returns a promise containing the Twilio Sync Token
 */
function getSyncToken() {
    const url = `${_this.baseUri}/twilio/sync/token`;
    const body = { identity: _this.identity };
    const requestConfig = {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'token': _this.token
        }
    };

    return fetch(url, requestConfig)
        .then((response) => {
            return response.json();
        })
        .then((jsonResponse) => {
            _this.syncToken = jsonResponse.token
            return jsonResponse.token;
        });
}

/**
 * Reinitializes the client by updating the token
 * @returns {Promise<void>} - Returns an empty promise... just like my ex
 */
function reinit() {
    if (!_this.syncClient) {
        return new Promise.resolve();
    }
    return getSyncToken().then((syncToken) => {
        return _this.syncClient.updateToken(syncToken)
            .then(() => {
                console.log('Reinitialized sync client');
            });
    })
}

function reconnect() {
    if (!_this.syncClient) {
        return new Promise.resolve();
    }
    console.log('Attempting to reconnect sync client...');
    return getSyncToken().then((syncToken) => {
        return _this.syncClient.updateToken(syncToken)
            .then(() => {
                try {
                    return _this.syncClient.connect().then(() => {
                        console.log('Sync client reconnected');
                    });
                } catch (error) {
                    console.error('Error reconnecting twilio sync client:', { error });
                }
            });
    })
}

/**
 * Sends a mutator to the sync doc to add data in a way that guards against race conditions
 * @param {Object} data - JSON data to add to the sync doc
 * @returns {Promise<boolean>} - Returns a boolean promise about the success of the mutate
 */
function mutateDoc(data) {
    return _this.syncClient.document(_this.documentId)
        .then(function (document) {
            return document.mutate(remoteData => Object.assign(remoteData, data))
                .then(() => { return true })
                .catch(error => {
                    console.error(error);
                    return false;
                });
        });
}

/**
 * Initializes the Twilio Sync Client that our client wraps
 * @returns {Promise<void>} - Returns an empty promise
 */
function init () {
    // First, we need to reach out the API and get a sync token for the client
    return getSyncToken()
        .then((syncToken) => {
            // instantiate the SyncClient provided from Twilio (which this client wraps)
            _this.syncClient = new SyncClient(syncToken);

            // get a new token if ours is ever about to expire
            _this.syncClient.on('tokenAboutToExpire', function() {
                reinit().then(() => {
                    console.log('Reinitialized');
                });
            });

            // we should never need this, as it should be taken care of above... but just in case!
            _this.syncClient.on('tokenExpired', function() {
                reinit().then(() => {
                    console.log('Reinitialized')
                });
            });

            _this.syncClient.on('connectionError', function(connectionError) {
                if (connectionError.terminal) {
                    console.error('Fatal error occurred with sync client:', connectionError.message);
                    // twilsock is going to stop connection attempts, so we will have to try to reconnect
                    // lets give it 3 seconds before trying
                    setTimeout(reconnect, 3000);
                }

            })

            // If there is any initial data, we should set that in the sync doc
            if (_this.initialData) {
                mutateDoc(_this.initialData).then(() => {
                    console.log('Initial Data Set in Sync Doc');
                });
            }


            // Set the listener on Twilio's client so we can emit our own custom events based on the document updating
            _this.syncClient.document(_this.documentId)
                .then(function(document) {
                    document.on('updated', function(doc) {
                        // First, lets emit the event for a general update
                        _this.eventEmitter.emit('spSyncDocUpdated', doc.data);
                        // Now we want to see if the session is still alive, or if it was ended by the ghost
                        if (doc.data.forceQuit && !doc.data.sessionEnded) {
                            // If this occurs, that means that the session was ended from the ghost. We will emit an event
                            // so the session UI knows the session ended, and we can close everything out correctly
                            // from the student's view.
                            const hasListeners = _this.eventEmitter.emit('spSessionForceClosed');
                            if (hasListeners) {
                                // Because there are actions that will be added to end the session, we need to change sessionEnded
                                // to true to avoid an endless loop
                                mutateDoc({ sessionEnded: true });
                            }
                        }
                        // Next, lets check for any actions that were added
                        // Check for extension actions that should be taken
                        if (doc.data.extensionActions && doc.data.extensionActions.length > 0) {
                            // Action(s) were added to the array, lets emit the actions that need to be taken and remove them
                            // from the sync doc
                            const hasListeners = _this.eventEmitter.emit('extensionActionsAdded', doc.data.extensionActions);
                            if (hasListeners) {
                                // The emit function above returns whether or not there is a registered listener. We should only
                                // remove the actions from the action array if something is registered and listening
                                const mutatedDoc = {
                                    extensionActions: []
                                };

                                // Now we will check if any modality options are being set, if they are, lets add them to the
                                // modality options array
                                for (const action of doc.data.extensionActions) {
                                    if (action.action === 'applyModalityOptions') {
                                        let currentModalityOptions = doc.data.currentModalityOptions;
                                        const modalityOptionsToAdd = action.modalityOptions;
                                        for (const modalityOption of modalityOptionsToAdd) {
                                            if (currentModalityOptions.filter(modOption => modOption.name === modalityOption.name).length === 0) {
                                                // We do not have the modality option in our currentModalityOptions, so we will add it
                                                currentModalityOptions = [...currentModalityOptions, modalityOption];
                                            }
                                        }
                                        mutatedDoc.currentModalityOptions = currentModalityOptions;
                                    }
                                    if (action.action === 'removeModalityOptions') {
                                        let currentModalityOptions = mutatedDoc.currentModalityOptions || doc.data.currentModalityOptions;
                                        const modalityOptionsToRemove = action.modalityOptions;
                                        for (const modalityOption of modalityOptionsToRemove) {
                                            currentModalityOptions = currentModalityOptions.filter(function(modOption) {
                                                // this will filter out anything that has the same name as the modOption
                                                return modOption.name !== modalityOption.name;
                                            });
                                        }
                                        mutatedDoc.currentModalityOptions = currentModalityOptions;
                                    }
                                }
                                mutateDoc(mutatedDoc);
                            }
                        }
                        // Do the same thing for onboarding actions
                        if (doc.data.onboardingActions && doc.data.onboardingActions.length > 0) {
                            // Action(s) were added to the array, lets emit the actions that need to be taken and remove them
                            // from the sync doc
                            const hasListeners = _this.eventEmitter.emit('onboardingActionsAdded', doc.data.onboardingActions);
                            if (hasListeners) {
                                // The emit function above returns whether or not there is a registered listener. We should only
                                // remove the actions from the action array if something is registered and listening
                                mutateDoc({ onboardingActions: [] });
                            }
                        }
                        // Do the same thing for session actions
                        if (doc.data.sessionActions && doc.data.sessionActions.length > 0) {
                            // Action(s) were added to the array, lets emit the actions that need to be taken and remove them
                            // from the sync doc
                            const hasListeners = _this.eventEmitter.emit('sessionActionsAdded', doc.data.sessionActions);
                            if (hasListeners) {
                                // The emit function above returns whether or not there is a registered listener. We should only
                                // remove the actions from the action array if something is registered and listening
                                mutateDoc({ sessionActions: [] });
                            }
                        }
                    });
                });
        });
}

/**
 * Function to sort and set the actions in the sync doc with the necessary information
 * @param {Object[]} actionsArray - Array of actionTypes objects sent in from `addAction()` or `addActions()`
 * @returns {Promise<boolean>} - Returns a promise with a boolean of whether it was successfully added or not
 */
function setActions(actionsArray) {
    return _this.syncClient.document(_this.documentId)
        .then(function (document) {
            return document.mutate(remoteData => {
                // Now that we have the doc, lets make sure that the arrays exist for the actions to be added
                if ((remoteData.extensionActions && remoteData.extensionActions.constructor === Array) &&
                    (remoteData.onboardingActions && remoteData.onboardingActions.constructor === Array) &&
                    (remoteData.sessionActions && remoteData.sessionActions.constructor === Array)) {

                    // Our arrays exist, so we need to start sorting and getting any additional data for actions
                    // We have 3 different types of actions, so we will sort them into the proper arrays that they need to go to
                    let extensionActions = [];
                    let onboardingActions = [];
                    let sessionActions = [];

                    // if there are any actions that require sync doc data by path, we will use this function
                    // reduce functions are brutal, so we should avoid them... however, lets try to explain what is happening
                    // obj is the sync doc, and path is a string with dots to what element you would like.
                    // for example path could be 'sessionStart.exam.configuration.password' if you wish to get the
                    // exam password.
                    // The function will first split the string on the periods which creates an array of strings.
                    // This reduce function takes in four things: the previousValue, currentValue, initialValue, and a reducer function
                    // how those are structured look like this:
                    // .reduce((previousValue, currentValue) => reducerFunction, initialValue
                    // So, we can see that our "initialValue" is the sync doc, which allows us to start with ALL of the data.
                    // Then, we passed in a "reducer function" of '(previousValue || {})[currentValue] which will go through
                    // the array and apply the reference to each nested field over the initial value (sync doc). So, we can think
                    // of it logically running like this (using sessionStart.exam.configuration.password):
                    // Step 1. previousValue = initialValue => syncDoc[sessionStart] (returns the sessionStart object from the sync doc)
                    // Step 2. previousValue = syncDoc.sessionStart => syncDoc.sessionStart[exam] (returns exam object)
                    // Step 3. previousValue = syncDoc.sessionStart.exam => syncDoc.sessionStart.exam[configuration] (returns configuration object)
                    // Step 4. previousValue = syncDoc.sessionStart.exam.configuration => syncDoc.sessionStart.exam.configuration[password] (returns password field)
                    // Step 5. all elements in the array split from the path string were done, so it returns the value (being the password)
                    const getObjectByPath = (obj, path) => path.split(".")
                        .reduce((previousValue, currentValue) => (previousValue || {})[currentValue], obj);

                    // loop through each action to sort and get information as needed
                    for (const action of actionsArray) {
                        // first, we need to see if there is any needed data to complete this action from the sync doc
                        if (action.requiredSyncDocField) {
                            // There was a value for some needed data, so we will find that by the path provided in the actionType
                            action.syncDocField = getObjectByPath(remoteData, action.requiredSyncDocField);
                        }
                        const executor = action.executor || "NotAnExecutor";
                        switch (executor) {
                            case "extension":
                                extensionActions = [...extensionActions, action];
                                break;
                            case "onboarding":
                                onboardingActions = [...onboardingActions, action];
                                break;
                            case "session":
                                sessionActions = [...sessionActions, action];
                                break;
                            default:
                                console.error('Unknown executor detected while setting action');
                                break;
                        }
                    }


                    // Now that our actions are sorted and have the data it needs, we will add any extension actions if they exist
                    remoteData.extensionActions = extensionActions.length > 0 ?
                        [...remoteData.extensionActions, ...extensionActions] :
                        [...remoteData.extensionActions];

                    // same with onboarding actions
                    remoteData.onboardingActions = onboardingActions.length > 0 ?
                        [...remoteData.onboardingActions, ...onboardingActions] :
                        [...remoteData.onboardingActions];

                    // and session actions
                    remoteData.sessionActions = sessionActions.length > 0 ?
                        [...remoteData.sessionActions, ...sessionActions] :
                        [...remoteData.sessionActions];
                }
                return remoteData;
            })
                .then(() => { return Promise.resolve(true)})
                .catch(error => {
                    console.error(error);
                    return Promise.resolve(false);
                });
        });
}

// START OF FUNCTIONS AVAILABLE FROM THE CLIENT

/**
 * Sets data into the connected Twilio Sync Doc
 * @param {object} data - JSON data to be set in the sync doc
 * @returns {Promise<boolean>} - Returns promise of whether the data was successfully set in the sync doc
 */
TwilioSyncClient.prototype.setSync = function (data) {
    return mutateDoc(data).then((mutateResult) => {
        return mutateResult;
    });
}

/**
 * Gets data from the connected Twilio Sync Doc
 * @returns {Promise<null|Object>} - JSON Object in Sync Doc
 */
TwilioSyncClient.prototype.getSync = function () {
    return _this.syncClient.document(_this.documentId).then((syncDocument) => {
        return syncDocument === null ? null : syncDocument.data;
    });
}

/**
 * Returns the client's custom event emitter to listen to "spSyncDocUpdated" and "spActionsAdded" events
 * @returns {module:events.EventEmitter}
 */
TwilioSyncClient.prototype.getEventEmitter = function() {
    return _this.eventEmitter;
}

/**
 * Adds an action to the Sync Doc being managed
 * @param {Object} action - Action created from ActionType object
 * @returns {Promise<boolean>} - Returns a promise with a boolean of whether the action was successfully added
 */
TwilioSyncClient.prototype.addAction = function (action) {
    return setActions([action])
        .then((success) => {
            return success;
        });
}

/**
 * Batch operation to add multiple actions at once
 * @param { Object[] } actionsArray - Array containing action objects
 * @returns {Promise<boolean>} - Returns a promise with a boolean of whether the action was successfully added
 */
TwilioSyncClient.prototype.addActions = function (actionsArray) {
    if (!actionsArray || actionsArray.constructor !== Array) {
        return Promise.resolve(false);
    }
    return setActions(actionsArray)
        .then((success) => {
            return success;
        });
}

TwilioSyncClient.prototype.getSyncToken = function () {
    return _this.syncToken;
}

module.exports = TwilioSyncClient;